import {
  BeakerIcon,
  BookOpenIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  UserGroupIcon,
} from "@heroicons/react/24/outline";
import { TFunction } from "i18next";
import { Logs } from "lucide-react";
import { useTranslation } from "react-i18next";

import { getPermissions } from "@/common/Permissions";
import { NavigationLink, NavMain } from "@/components/ui/sidebar/nav-main";
import { usePermissions } from "@/context/PermissionContext";
import useAuthUser from "@/hooks/useAuthUser";
import { useCareApps } from "@/hooks/useCareApps";
import useCurrentFacility from "@/pages/Facility/utils/useCurrentFacility";
import { FacilityBareMinimum } from "@/types/facility/facility";
import careConfig from "@careConfig";

interface FacilityNavProps {
  selectedFacility: FacilityBareMinimum | null;
}

type FacilityNavigationPermissions = ReturnType<typeof getPermissions> & {
  canListDevices: boolean;
  canManageDevices: boolean;
  canReadActivityDefinition: boolean;
  canWriteActivityDefinition: boolean;
  canReadObservationDefinition: boolean;
  canWriteObservationDefinition: boolean;
  canReadSpecimenDefinition: boolean;
  canWriteSpecimenDefinition: boolean;
  canReadProductKnowledge: boolean;
  canWriteProductKnowledge: boolean;
  canReadProduct: boolean;
  canWriteProduct: boolean;
  canReadChargeItemDefinition: boolean;
  canWriteChargeItemDefinition: boolean;
  canReadPatientIdentifierConfig: boolean;
  canWritePatientIdentifierConfig: boolean;
  canReadTagConfig: boolean;
  canWriteTagConfig: boolean;
};

function generateFacilityLinks(
  selectedFacility: FacilityBareMinimum | null,
  t: TFunction,
  permissions: FacilityNavigationPermissions,
  pluginLinks: NavigationLink[],
  pluginBillingLinks: NavigationLink[],
  hasPractitionerWorkspace: boolean,
) {
  if (!selectedFacility) return [];

  const encounterClasses = careConfig.encounterClasses;

  const baseUrl = `/facility/${selectedFacility.id}`;
  const canUseEncounterRead =
    permissions.canListEncounters ||
    permissions.canReadEncounter ||
    permissions.canReadEncounterClinicalData;
  const canUseAccounts = permissions.canReadAccount;
  const canUseInvoices = permissions.canReadInvoice;
  const canUsePayments = permissions.canReadPaymentReconciliation;
  const canUsePatientArea =
    permissions.canViewPatients ||
    permissions.canWritePatient ||
    permissions.canCreatePatient ||
    permissions.canWriteAppointment ||
    permissions.canCreateEncounter ||
    canUseAccounts ||
    canUseEncounterRead;
  const canUseQueueArea =
    permissions.canViewAppointments ||
    permissions.canListTokens ||
    permissions.canWriteToken;
  const canUseServices =
    permissions.canReadHealthcareService ||
    permissions.canWriteHealthcareService;
  const canUseResource =
    permissions.canReadResourceCategory || permissions.canWriteResourceCategory;
  const canUseUsers = permissions.canListUsers || permissions.canCreateUser;
  const billingChildren: NavigationLink[] = [
    {
      name: t("accounts"),
      url: `${baseUrl}/billing/account`,
      visibility: canUseAccounts,
    },
    {
      name: t("invoices"),
      url: `${baseUrl}/billing/invoices`,
      visibility: canUseInvoices,
    },
    {
      name: t("payments"),
      url: `${baseUrl}/billing/payments`,
      visibility: canUsePayments,
    },
    ...pluginBillingLinks.map((l) => ({
      ...l,
      url: `${baseUrl}${l.url}`,
    })),
  ].filter((link) => link.visibility !== false);

  const settingsChildren: NavigationLink[] = [
    {
      name: t("general"),
      url: `${baseUrl}/settings/general`,
      visibility:
        permissions.canViewFacilityGeneralSettings ||
        permissions.canUpdateFacility,
    },
    {
      name: t("departments"),
      url: `${baseUrl}/settings/departments`,
      visibility:
        permissions.canViewFacilityOrganizations ||
        permissions.canCreateFacilityOrganization ||
        permissions.canManageFacilityOrganization,
    },
    {
      name: t("locations"),
      url: `${baseUrl}/settings/locations`,
      visibility:
        permissions.canListFacilityLocations ||
        permissions.canWriteFacilityLocation,
    },
    {
      name: t("devices"),
      url: `${baseUrl}/settings/devices`,
      visibility: permissions.canListDevices || permissions.canManageDevices,
    },
    {
      name: t("specimen_definitions"),
      url: `${baseUrl}/settings/specimen_definitions`,
      visibility:
        permissions.canReadSpecimenDefinition ||
        permissions.canWriteSpecimenDefinition,
    },
    {
      name: t("observation_definitions"),
      url: `${baseUrl}/settings/observation_definitions`,
      visibility:
        permissions.canReadObservationDefinition ||
        permissions.canWriteObservationDefinition,
    },
    {
      name: t("activity_definitions"),
      url: `${baseUrl}/settings/activity_definitions`,
      visibility:
        permissions.canReadActivityDefinition ||
        permissions.canWriteActivityDefinition,
    },
    {
      name: t("billing"),
      url: `${baseUrl}/settings/billing`,
      visibility:
        permissions.canReadChargeItemDefinition ||
        permissions.canWriteChargeItemDefinition ||
        permissions.canSetChargeItemDefinition,
    },
    {
      name: t("charge_item_definitions"),
      url: `${baseUrl}/settings/charge_item_definitions`,
      visibility:
        permissions.canReadChargeItemDefinition ||
        permissions.canWriteChargeItemDefinition ||
        permissions.canSetChargeItemDefinition,
    },
    {
      name: t("healthcare_services"),
      url: `${baseUrl}/settings/healthcare_services`,
      visibility:
        permissions.canReadHealthcareService ||
        permissions.canWriteHealthcareService,
    },
    {
      name: t("product_knowledge"),
      url: `${baseUrl}/settings/product_knowledge`,
      visibility:
        permissions.canReadProductKnowledge ||
        permissions.canWriteProductKnowledge,
    },
    {
      name: t("product"),
      url: `${baseUrl}/settings/product`,
      visibility: permissions.canReadProduct || permissions.canWriteProduct,
    },
    {
      name: t("token_category"),
      url: `${baseUrl}/settings/token_category`,
      visibility:
        permissions.canListTokenCategories || permissions.canWriteTokenCategory,
    },
    {
      name: t("patient_identifier_config"),
      url: `${baseUrl}/settings/patient_identifier_config`,
      visibility:
        permissions.canReadPatientIdentifierConfig ||
        permissions.canWritePatientIdentifierConfig,
    },
    {
      name: t("tag_config"),
      url: `${baseUrl}/settings/tag_config`,
      visibility: permissions.canReadTagConfig || permissions.canWriteTagConfig,
    },
    {
      name: t("templates"),
      url: `${baseUrl}/template`,
      visibility:
        permissions.canListTemplate ||
        permissions.canWriteTemplate ||
        permissions.canManageTemplate,
    },
  ].filter((link) => link.visibility !== false);

  const links: NavigationLink[] = [
    {
      name: t("overview"),
      url: `${baseUrl}/overview`,
      icon: <BuildingOfficeIcon className="h-4 w-4" />,
    },
    {
      name: t("appointments"),
      url: `${baseUrl}/appointments`,
      icon: <CalendarDaysIcon className="h-4 w-4" />,
      visibility: permissions.canViewAppointments,
    },
    {
      name: t("queues"),
      url: `${baseUrl}/queues`,
      icon: <Logs className="h-4 w-4" />,
      visibility: canUseQueueArea && !hasPractitionerWorkspace,
    },
    {
      name: t("patients"),
      url: `${baseUrl}/patients`,
      icon: <UserGroupIcon className="h-4 w-4" />,
      visibility: canUsePatientArea,
      children: [
        {
          name: t("search_patients"),
          url: `${baseUrl}/patients`,
          visibility: permissions.canViewPatients || canUseAccounts,
        },
        {
          name: t("all_encounters"),
          url: `${baseUrl}/encounters/patients/all`,
          visibility: canUseEncounterRead && encounterClasses.length > 1,
        },
        ...encounterClasses.map((encounterClass) => ({
          name: t(`encounter_class_encounters`, {
            encounterClassName: t(`encounter_class__${encounterClass}`),
          }),
          url: `${baseUrl}/encounters/patients/${encounterClass}`,
          visibility: canUseEncounterRead,
        })),
        {
          name: t("locations"),
          url: `${baseUrl}/encounters/locations`,
          visibility: canUseEncounterRead,
        },
      ],
    },
    {
      name: t("services"),
      url: `${baseUrl}/services`,
      icon: <BeakerIcon className="h-4 w-4" />,
      visibility: canUseServices && !hasPractitionerWorkspace,
    },
    {
      name: t("resource"),
      url: `${baseUrl}/resource`,
      icon: <BookOpenIcon className="h-4 w-4" />,
      visibility: canUseResource && !hasPractitionerWorkspace,
    },
    {
      name: t("users"),
      url: `${baseUrl}/users`,
      icon: <UserGroupIcon className="h-4 w-4" />,
      visibility: canUseUsers && !hasPractitionerWorkspace,
    },
    {
      name: t("billing"),
      url: `${baseUrl}/billing`,
      icon: <CreditCardIcon className="h-4 w-4" />,
      visibility: billingChildren.length > 0 && !hasPractitionerWorkspace,
      children: billingChildren,
    },
    {
      name: t("settings"),
      url: `${baseUrl}/settings/general`,
      icon: <Cog6ToothIcon className="h-4 w-4" />,
      visibility: settingsChildren.length > 0 && !hasPractitionerWorkspace,
      children: settingsChildren,
    },
  ];

  return [
    ...links,
    ...pluginLinks.map((l) => ({
      ...l,
      url: `${baseUrl}/${l.url}`,
    })),
  ];
}

export function FacilityNav({ selectedFacility }: FacilityNavProps) {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const careApps = useCareApps();
  const pluginNavItems = careApps.flatMap((c) =>
    !c.isLoading && c.navItems ? c.navItems : [],
  ) as NavigationLink[];

  const pluginBillingNavItems = careApps.flatMap((c) =>
    !c.isLoading && c.billingNavItems ? c.billingNavItems : [],
  ) as NavigationLink[];

  const { facility } = useCurrentFacility();

  const facilityPermissions = facility?.permissions ?? [];
  const hasFacilityPermission = (permission: string) =>
    hasPermission(permission, facilityPermissions);
  const permissions: FacilityNavigationPermissions = {
    ...getPermissions(hasPermission, facilityPermissions),
    canListDevices: hasFacilityPermission("can_list_devices"),
    canManageDevices: hasFacilityPermission("can_manage_devices"),
    canReadActivityDefinition: hasFacilityPermission(
      "can_read_activity_definition",
    ),
    canWriteActivityDefinition: hasFacilityPermission(
      "can_write_activity_definition",
    ),
    canReadObservationDefinition: hasFacilityPermission(
      "can_read_observation_definition",
    ),
    canWriteObservationDefinition: hasFacilityPermission(
      "can_write_observation_definition",
    ),
    canReadSpecimenDefinition: hasFacilityPermission(
      "can_read_specimen_definition",
    ),
    canWriteSpecimenDefinition: hasFacilityPermission(
      "can_write_specimen_definition",
    ),
    canReadProductKnowledge: hasFacilityPermission(
      "can_read_product_knowledge",
    ),
    canWriteProductKnowledge: hasFacilityPermission(
      "can_write_product_knowledge",
    ),
    canReadProduct: hasFacilityPermission("can_read_product"),
    canWriteProduct: hasFacilityPermission("can_write_product"),
    canReadChargeItemDefinition: hasFacilityPermission(
      "can_read_charge_item_definition",
    ),
    canWriteChargeItemDefinition: hasFacilityPermission(
      "can_write_charge_item_definition",
    ),
    canReadPatientIdentifierConfig: hasFacilityPermission(
      "can_read_patient_identifier_config",
    ),
    canWritePatientIdentifierConfig: hasFacilityPermission(
      "can_write_patient_identifier_config",
    ),
    canReadTagConfig: hasFacilityPermission("can_read_tag_config"),
    canWriteTagConfig: hasFacilityPermission("can_write_tag_config"),
  };
  const user = useAuthUser();
  return (
    <NavMain
      links={generateFacilityLinks(
        selectedFacility,
        t,
        permissions,
        pluginNavItems,
        pluginBillingNavItems,
        // Treat superusers as not being forced into practitioner-only UI
        permissions.canAccessPractitionerWorkspace && !user?.is_superuser,
      )}
    />
  );
}
